import React from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from '../types';
import { Loader2 } from 'lucide-react';
import { imageContainer, loadingContainer } from '../styles/index';

interface CropImageRendererProps {
  isImageLoaded: boolean;
  cropPosition: Point;
  zoom: number;
  croppedArea: Area | null;
  aspectRatioValue: number | null;
  imageUrl: string;
  imgRef: React.RefObject<HTMLImageElement>;
  onCropChange: (position: Point) => void;
  onZoomChange: (zoom: number) => void;
  onCroppedAreaChange: (area: Area | null, areaPixels: Area | null) => void;
  onImageLoad: () => void;
  loadingMessage: string;
}

export const CropImageRenderer: React.FC<CropImageRendererProps> = ({
  isImageLoaded,
  cropPosition,
  zoom,
  aspectRatioValue,
  imageUrl,
  onCropChange,
  onZoomChange,
  onCroppedAreaChange,
  onImageLoad,
  loadingMessage,
}) => {
  if (!isImageLoaded) {
    return (
      <div className={imageContainer}>
        <div className={loadingContainer}>
          <Loader2 className="animate-spin" size={32} />
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  const handleCropComplete = (croppedArea: Area, croppedAreaPixels: Area): void => {
    onCroppedAreaChange(croppedArea, croppedAreaPixels);
  };

  const handleCropAreaChange = (croppedArea: Area, croppedAreaPixels: Area): void => {
    onCroppedAreaChange(croppedArea, croppedAreaPixels);
  };

  return (
    <div className={imageContainer}>
      <Cropper
        image={imageUrl}
        crop={cropPosition}
        zoom={zoom}
        aspect={aspectRatioValue || undefined}
        onCropChange={onCropChange}
        onZoomChange={onZoomChange}
        onCropComplete={handleCropComplete}
        onCropAreaChange={handleCropAreaChange}
        zoomWithScroll={true}
        showGrid={true}
        style={{
          containerStyle: {
            position: 'relative',
            width: '100%',
            height: '100%',
          },
        }}
        onMediaLoaded={() => {
          // Call onImageLoad when the image is loaded
          onImageLoad();
        }}
      />
    </div>
  );
};
